import { t } from "@lingui/macro";
import { useMutation, useQuery } from "@tanstack/react-query";

import { toast } from "@/client/hooks/use-toast";
import { axios } from "@/client/libs/axios";

// Checkout/portal actions redirect the browser; if they fail, surface the error
// instead of silently doing nothing.
const notifyBillingError = (error: unknown) => {
  toast({
    variant: "error",
    title: t`Payment could not be started`,
    description: (error as Error).message,
  });
};

export type Subscription = {
  plan: string;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  billingEnabled: boolean;
  templatesAccess: boolean;
  templatesPassEnd: string | null;
};

export const fetchSubscription = async () => {
  const response = await axios.get<Subscription>("/billing/subscription");

  return response.data;
};

export const useSubscription = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["subscription"],
    queryFn: fetchSubscription,
  });

  return { subscription: data, loading: isLoading };
};

export type PaidPlan = "pro" | "autopilot";

// Both actions return a hosted Stripe URL we redirect the browser to.
export const createCheckout = async (plan: PaidPlan = "pro") => {
  const response = await axios.post<{ url: string }>("/billing/checkout", { plan });

  return response.data.url;
};

// Weekly templates pass: unlocks premium templates only.
export const startTemplatesPass = async () => {
  const response = await axios.post<{ url: string }>("/billing/templates-pass/checkout");

  return response.data.url;
};

export const useTemplatesPass = () => {
  const { mutateAsync: subscribe, isPending: loading } = useMutation({
    mutationFn: startTemplatesPass,
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: notifyBillingError,
  });

  return { subscribe, loading };
};

export const openBillingPortal = async () => {
  const response = await axios.post<{ url: string }>("/billing/portal");

  return response.data.url;
};

export const useCheckout = () => {
  const { mutateAsync: checkout, isPending: loading } = useMutation({
    mutationFn: createCheckout,
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: notifyBillingError,
  });

  return { checkout, loading };
};

// Buy AI token packs for the user's own balance (one-time payment).
export const buyTokens = async (quantity = 1) => {
  const response = await axios.post<{ url: string }>("/billing/tokens/checkout", { quantity });

  return response.data.url;
};

export const useBuyTokens = () => {
  const { mutateAsync: purchase, isPending: loading } = useMutation({
    mutationFn: buyTokens,
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: notifyBillingError,
  });

  return { purchase, loading };
};

export const useBillingPortal = () => {
  const { mutateAsync: openPortal, isPending: loading } = useMutation({
    mutationFn: openBillingPortal,
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: notifyBillingError,
  });

  return { openPortal, loading };
};
